import React, { useState } from 'react'
import { PingLogo, HealthStatus } from '../atoms/PingLogo'
import { ModalBackdrop } from '../atoms/ModalBackdrop'
import { Card } from '../atoms/Card'
import { StatusIndicator } from '../atoms/StatusIndicator'
import { useHealthStatus } from '../../hooks/useHealthStatus'
import { useApiService } from '../../services/apiService'

interface LogoWithStatusProps {
  status?: HealthStatus
  size?: 'small' | 'medium' | 'medium-large' | 'large'
  className?: string
  'data-testid'?: string
}

interface ComponentHealthDisplay {
  name: string
  status: HealthStatus
  message: string
  response_time?: number
  details?: Record<string, any>
}

export const LogoWithStatus: React.FC<LogoWithStatusProps> = ({
  status: propStatus,
  size = 'medium',
  className = '',
  'data-testid': testId = undefined,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  
  // Initialize API service
  useApiService()
  
  // Use health status hook
  const { 
    basicHealth, 
    detailedHealth, 
    healthSummary, 
    logoStatus, 
    statusMessage, 
    isLoading, 
    error,
    refreshDetailed 
  } = useHealthStatus({ 
    fetchSummary: true, 
    fetchDetailed: false 
  })

  // Use prop status or health service status
  const currentStatus = propStatus || logoStatus

  const statusMessages = {
    operational: 'All systems operational',
    warning: 'Warning: Some issues detected',
    critical: 'Critical: System issues detected',
    unknown: 'Status unknown'
  }

  const statusColors = {
    operational: 'text-green-600 dark:text-green-400',
    warning: 'text-amber-600 dark:text-amber-400',
    critical: 'text-red-600 dark:text-red-400',
    unknown: 'text-gray-600 dark:text-gray-400'
  }

  const getStatusVariant = (status: HealthStatus) => {
    switch (status) {
      case 'operational':
        return 'success'
      case 'warning':
        return 'warning'
      case 'critical':
        return 'error'
      default:
        return 'primary'
    }
  }

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (days > 0) {
      return `${days} days, ${hours} hours, ${minutes} minutes`
    } else if (hours > 0) {
      return `${hours} hours, ${minutes} minutes`
    } else {
      return `${minutes} minutes`
    }
  }

  const formatResponseTime = (ms?: number): string => {
    if (!ms) return 'N/A'
    return `${Math.round(ms)}ms`
  }

  const getComponentHealthDisplay = (): ComponentHealthDisplay[] => {
    if (!detailedHealth) return []

    const components: ComponentHealthDisplay[] = [
      {
        name: 'Database',
        status: detailedHealth.database.status,
        message: detailedHealth.database.message,
        response_time: detailedHealth.database.response_time_ms,
        details: {
          version: detailedHealth.database.database_version,
          pool_size: detailedHealth.database.connection_pool_size,
          active_connections: detailedHealth.database.active_connections,
        }
      },
      {
        name: 'Auth0',
        status: detailedHealth.auth0.status,
        message: detailedHealth.auth0.message,
        response_time: detailedHealth.auth0.response_time_ms,
        details: {
          management_api: detailedHealth.auth0.management_api_available ? 'Available' : 'Unavailable',
          token_cache_size: detailedHealth.auth0.token_cache_size,
          last_token_refresh: detailedHealth.auth0.last_token_refresh,
        }
      },
      {
        name: 'System',
        status: detailedHealth.system.status,
        message: detailedHealth.system.message,
        response_time: detailedHealth.system.response_time_ms,
        details: {
          cpu_usage: detailedHealth.system.cpu_usage_percent ? `${detailedHealth.system.cpu_usage_percent.toFixed(1)}%` : 'N/A',
          memory_usage: detailedHealth.system.memory_usage_percent ? `${detailedHealth.system.memory_usage_percent.toFixed(1)}%` : 'N/A',
          disk_usage: detailedHealth.system.disk_usage_percent ? `${detailedHealth.system.disk_usage_percent.toFixed(1)}%` : 'N/A',
        }
      }
    ]

    // Add additional services
    detailedHealth.services.forEach(service => {
      components.push({
        name: service.name,
        status: service.status,
        message: service.message,
        response_time: service.response_time_ms,
        details: {
          cache_hit_rate: service.cache_hit_rate ? `${service.cache_hit_rate.toFixed(1)}%` : 'N/A',
          sync_queue_size: service.sync_queue_size,
          last_sync: service.last_sync_time,
        }
      })
    })

    return components
  }

  const handleLogoInteraction = async (e: React.MouseEvent) => {
    e.preventDefault()
    
    // Open modal
    setIsModalOpen(true)
    
    // Load detailed status if not already loaded
    if (!detailedHealth) {
      setIsLoadingDetails(true)
      try {
        await refreshDetailed()
      } catch (error) {
        console.error('Failed to load detailed status:', error)
      } finally {
        setIsLoadingDetails(false)
      }
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  // Handle keyboard accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleLogoInteraction(e as any)
    }
  }

  const componentHealthData = getComponentHealthDisplay()

  return (
    <>
      <div 
        className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg ${className}`}
        onClick={handleLogoInteraction}
        onContextMenu={handleLogoInteraction} // Right-click support
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Ping Pong Feedback System - Status: ${currentStatus}. Click to view details.`}
        data-testid={testId}
      >
        <PingLogo 
          status={currentStatus}
          size={size}
          data-testid={testId ? `${testId}-logo` : undefined}
        />
      </div>

      {/* Detailed Status Modal */}
      <ModalBackdrop 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
        blurBackground={true}
      >
        <div 
          className="transform transition-all duration-200 scale-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-auto" data-testid="logo-status-modal">
            {/* Modal Header with Close Button */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <StatusIndicator 
                  status={getStatusVariant(currentStatus)} 
                  className="mr-1" 
                />
                <div>
                  <h2 className="text-h3 font-semibold">
                    {basicHealth?.service || 'Ping Pong Feedback System'}
                  </h2>
                  <p className={`text-sm ${statusColors[currentStatus]}`}>
                    {statusMessage || statusMessages[currentStatus]}
                  </p>
                </div>
              </div>
              
              {/* Close Button */}
              <button
                onClick={handleCloseModal}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close status details"
                data-testid="close-modal-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {isLoadingDetails || isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading details...</span>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-600 dark:text-red-400 mb-4">
                    Failed to load system details: {error}
                  </p>
                  <button
                    onClick={refreshDetailed}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* System Overview */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                      System Overview
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {basicHealth?.version || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Version
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {detailedHealth ? formatUptime(detailedHealth.uptime_seconds) : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Uptime
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {detailedHealth?.healthy_components || 0}/{detailedHealth?.total_components || 0}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Healthy Components
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                          {formatResponseTime(detailedHealth?.avg_response_time_ms)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Avg Response Time
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Component Health */}
                  {componentHealthData.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                        Component Health
                      </h3>
                      <div className="space-y-3">
                        {componentHealthData.map((component, index) => (
                          <div 
                            key={index}
                            className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <StatusIndicator 
                                  status={getStatusVariant(component.status)} 
                                  className="mr-1" 
                                />
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {component.name}
                                </span>
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {formatResponseTime(component.response_time)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              {component.message}
                            </p>
                            {component.details && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                {Object.entries(component.details).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                                    <span>{value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Issues Summary */}
                  {healthSummary && (healthSummary.critical_issues.length > 0 || healthSummary.warning_issues.length > 0) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                        Issues Summary
                      </h3>
                      
                      {healthSummary.critical_issues.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">
                            Critical Issues
                          </h4>
                          <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                            {healthSummary.critical_issues.map((issue, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-red-500 mt-1">•</span>
                                <span>{issue}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {healthSummary.warning_issues.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">
                            Warning Issues
                          </h4>
                          <ul className="text-sm text-amber-600 dark:text-amber-400 space-y-1">
                            {healthSummary.warning_issues.map((issue, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-amber-500 mt-1">•</span>
                                <span>{issue}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Last Updated */}
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center pt-4 border-t border-gray-200 dark:border-gray-700">
                    Last updated: {basicHealth?.timestamp ? new Date(basicHealth.timestamp).toLocaleString() : 'Unknown'}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </ModalBackdrop>
    </>
  )
} 